import { useEffect, useState } from 'react';
import MediaQuery from 'react-responsive';
import ApiDatabase from 'server';
import { Plus, Filter, CogIcon } from '../../assets/icons';
import AdminSearchBar from '../../components/AdminSearch/AdminSearchBar';
import AdminSearchInput from '../../components/AdminSearch/AdminSearchInput';
import SearchTypeSelect from '../../components/AdminSearch/SearchTypeSelect';
import { COMPANY_SEARCH_OPTIONS } from '../../components/AdminSearch/adminSearchOptions';
import {
  COMPANY_COLUMNS_DEFAULT,
  COMPANY_COLUMNS_STORAGE_KEY,
  COMPANY_COLUMN_DEFINITIONS,
} from '../../components/modal/dataModal/data/modalCompany/ModalCustomizeCompanyColumns';
import useModal from '../../components/modal/useModal';
import Button from '../../components/button';
import PageForm from '../../components/pageForm/PageForm';
import SubNavbar from '../../components/SubNavbar/SubNavbar';
import Pagination from '../../components/pagination/pagination';
import useSlideOver from '../../components/SlideOver/useSlideOver';
import Table from '../../components/table/table';
import CompanyMobileCard from '../../components/card/MobileListCard/CompanyMobileCard';
import UseContext from '../../hooks/useContext';
import BottomSheet from '../../components/bottomSheet/BottomSheet';
import EmptyState from '../../components/EmptyState/EmptyState';
import CompanyFilterPanel from './Company/CompanyFilterPanel';

const buildTitles = (activeColumns) => {
  const labels = activeColumns
    .map((key) => COMPANY_COLUMN_DEFINITIONS.find((c) => c.key === key)?.label)
    .filter(Boolean)
    .map((l) => l.toUpperCase());
  return ['ENSEIGNE', ...labels];
};

const MobileCardSkeleton = () => (
  <div className="flex flex-col gap-3 p-4 animate-pulse">
    {[1, 2, 3, 4, 5].map((i) => (
      <div key={i} className="bg-white rounded-xl border border-gray-200 p-4">
        <div className="h-5 bg-gray-200 rounded w-3/4 mb-3" />
        <div className="flex justify-between items-center">
          <div className="h-4 bg-gray-100 rounded w-24" />
          <div className="h-4 bg-gray-100 rounded w-28" />
        </div>
      </div>
    ))}
  </div>
);

const TableSkeleton = ({ columnsCount = 3 }) => (
  <table className="animate-pulse w-full">
    <tbody>
      {[1, 2, 3, 4, 5].map((i) => (
        <tr key={i}>
          {Array.from({ length: columnsCount }).map((_, idx) => (
            <td
              key={idx}
              className={`py-7 ${idx === 0 ? 'pl-4' : ''} ${idx === columnsCount - 1 ? 'pr-3' : 'px-2'}`}
            >
              <div className="h-2 bg-gray-200 rounded" />
            </td>
          ))}
        </tr>
      ))}
    </tbody>
  </table>
);

const Company = () => {
  const [isWaiting, setIsWaiting] = useState(true);
  const [companies, setCompanies] = useState([]);
  const { toggleSlideOver } = useSlideOver();
  const { setObjID, modalData, setModalData, modalDataReturn, setModalDataReturn } = UseContext();
  const { toggle } = useModal();

  const [activeColumns, setActiveColumns] = useState(() => {
    try {
      const saved = localStorage.getItem(COMPANY_COLUMNS_STORAGE_KEY);
      return saved ? JSON.parse(saved) : COMPANY_COLUMNS_DEFAULT;
    } catch {
      return COMPANY_COLUMNS_DEFAULT;
    }
  });
  const titles = buildTitles(activeColumns);

  const openCustomizeColumns = () => {
    setModalData({ activeColumns });
    toggle('company_customize_columns');
  };

  useEffect(() => {
    if (modalDataReturn?.type === 'company_columns_updated' && Array.isArray(modalDataReturn.activeColumns)) {
      setActiveColumns(modalDataReturn.activeColumns);
      setModalDataReturn(null);
    }
  }, [modalDataReturn]);
  const [search, setSearch] = useState('');
  const [optionSearch, setOptionSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [countPerPage, setCountPerPage] = useState(0);
  const limitElement = 20;
  const [countTotal, setCountTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [isFilterPanelOpen, setIsFilterPanelOpen] = useState(false);
  const [tempSearch, setTempSearch] = useState('');
  const [tempOptionSearch, setTempOptionSearch] = useState('');

  const [departmentOptions, setDepartmentOptions] = useState([]);

  const DEFAULT_PANEL_FILTERS = {
    missionCountMode: '',
    missionCountMin: '',
    incompleteRemuneration: false,
    departments: [],
    filterCategory: '',
  };
  const [panelFilters, setPanelFilters] = useState(DEFAULT_PANEL_FILTERS);
  const [draftPanelFilters, setDraftPanelFilters] = useState(DEFAULT_PANEL_FILTERS);

  const activePanelFilterCount = (
    (panelFilters.missionCountMode ? 1 : 0)
    + (panelFilters.incompleteRemuneration ? 1 : 0)
    + ((panelFilters.departments || []).filter((d) => d.checked).length > 0 ? 1 : 0)
    + (panelFilters.filterCategory ? 1 : 0)
  );

  useEffect(() => {
    ApiDatabase.getCityDepartments(
      { token },
      (data) => {
        const opts = [
          ...data.map((d) => ({ value: d.toLowerCase(), label: d, checked: false })),
          { value: 'other', label: 'Aucun', checked: false },
        ];
        setDepartmentOptions(opts);
        setPanelFilters((prev) => ({ ...prev, departments: opts }));
        setDraftPanelFilters((prev) => ({ ...prev, departments: opts }));
      },
      () => {}
    );
  }, []);

  const token = localStorage.getItem('xsrfToken');

  const openFilterSheet = () => {
    setTempSearch(search);
    setTempOptionSearch(optionSearch);
    setIsFilterOpen(true);
  };

  const applyFilters = () => {
    setSearch(tempSearch);
    setOptionSearch(tempOptionSearch);
    setIsFilterOpen(false);
  };

  const openFilterPanel = () => {
    setDraftPanelFilters(panelFilters);
    setIsFilterPanelOpen(true);
  };
  const applyPanelFilters = () => {
    setPanelFilters(draftPanelFilters);
    setIsFilterPanelOpen(false);
  };
  const resetPanelFilters = () =>
    setDraftPanelFilters({ ...DEFAULT_PANEL_FILTERS, departments: departmentOptions });

  const manageCompaniesSearch = () => {
    setIsWaiting(true);
    ApiDatabase.getCompanies(
      {
        token,
        search,
        optionSearch,
        filterCategory: panelFilters.filterCategory || undefined,
        page: currentPage,
        limit: limitElement,
        missionCountMode: panelFilters.missionCountMode || undefined,
        missionCountMin: panelFilters.missionCountMin || undefined,
        incompleteRemuneration: panelFilters.incompleteRemuneration ? 'true' : undefined,
        departments: (() => {
          const selected = (panelFilters.departments || [])
            .filter((d) => d.checked)
            .map((d) => d.value);
          return selected.length > 0 ? JSON.stringify(selected) : undefined;
        })(),
      },
      (data) => {
        setCompanies(data.companies);
        setTotalPages(data.countPages);
        setCountPerPage(data.countPerPage);
        setCountTotal(data.count);
        setIsWaiting(false);
      },
      (err) => { if (err.name !== 'CanceledError') console.error(err); }
    );
  };

  useEffect(() => {
    manageCompaniesSearch();
  }, [currentPage, limitElement]);

  useEffect(() => {
    setCurrentPage(1);
    const timer = setTimeout(manageCompaniesSearch, 400);
    return () => clearTimeout(timer);
  }, [search, optionSearch, panelFilters]);

  const openCompanyInfo = (id) => {
    setObjID(id);
    toggleSlideOver('get_company');
  };

  return (
    <PageForm>
      <SubNavbar>
          <div className="flex justify-between items-center w-full gap-2 md:gap-3 min-w-0">
            <MediaQuery minWidth={768}>
              <div className="flex gap-2 items-center flex-1 min-w-0">
                <AdminSearchBar
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  optionValue={optionSearch}
                  onOptionChange={setOptionSearch}
                  searchOptions={COMPANY_SEARCH_OPTIONS}
                  showTypeSelect
                  showSearchRules
                  inputWrapperClassName="flex-1 min-w-[120px] max-w-[400px]"
                  selectWrapperClassName="flex-shrink-0 w-[160px] md:max-w-[220px]"
                />
              </div>
          </MediaQuery>

          <MediaQuery maxWidth={767}>
            <Button color="SECONDARY" size="SM" onClick={openFilterSheet}>
              <Filter wh={16} color="#374151" />
              <span>Filtrer</span>
            </Button>
          </MediaQuery>

          <div className="flex items-center gap-3 flex-shrink-0">
            <MediaQuery minWidth={768}>
              <div className="flex items-center gap-2">
                <Button color="SECONDARY" size="ICON" onClick={openCustomizeColumns}>
                  <CogIcon wh={20} color="#374151" />
                </Button>
                <Button color="SECONDARY" size="SM" onClick={openFilterPanel}>
                  <Filter wh={16} color="#374151" />
                  <span>Filtrer{activePanelFilterCount > 0 ? ` (${activePanelFilterCount})` : ''}</span>
                </Button>
              </div>
            </MediaQuery>
            <Button size="SM" color="PRIMARY" className="h-10" onClick={() => toggleSlideOver('add_company')}>
              <Plus wh={18} color="#FFFFFF" />
              <span>Ajouter</span>
            </Button>
          </div>
        </div>
      </SubNavbar>

        <div className="flex flex-col w-full h-full overflow-hidden">
          <div className="flex-1 overflow-y-auto md:overflow-y-hidden">
            {isWaiting ? (
              <>
                <MediaQuery minWidth={768}><TableSkeleton columnsCount={titles.length} /></MediaQuery>
                <MediaQuery maxWidth={767}><MobileCardSkeleton /></MediaQuery>
              </>
            ) : companies.length > 0 ? (
              <>
                <MediaQuery minWidth={768}>
                  <Table champsBody={companies} champsHeader={titles} activeColumns={activeColumns} />
                </MediaQuery>
                <MediaQuery maxWidth={767}>
                  <div className="flex flex-col gap-3 p-4">
                    {companies.map((item) => (
                      <CompanyMobileCard key={item._id} item={item} onOpen={openCompanyInfo} />
                    ))}
                  </div>
                </MediaQuery>
              </>
            ) : (
              <EmptyState type="company" />
            )}
          </div>
        </div>

        {totalPages > 1 && (
          <div className="flex-shrink-0 border-t border-gray-100 bg-white px-4 md:px-8 py-4">
            <Pagination
              currentPage={currentPage}
              countPerPage={countPerPage}
              countTotal={countTotal}
              totalPages={totalPages}
              setCurrentPage={setCurrentPage}
            />
          </div>
        )}

        <CompanyFilterPanel
          isOpen={isFilterPanelOpen}
          onClose={() => setIsFilterPanelOpen(false)}
          filters={draftPanelFilters}
          setFilters={setDraftPanelFilters}
          onApply={applyPanelFilters}
          onReset={resetPanelFilters}
          activeCount={activePanelFilterCount}
        />

        <BottomSheet isOpen={isFilterOpen} onClose={() => setIsFilterOpen(false)} title="Filtrer">
          <div className="flex flex-col gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Rechercher</label>
              <AdminSearchInput
                placeholder="Rechercher..."
                showSearchRules
                value={tempSearch}
                onChange={(e) => setTempSearch(e.target.value)}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Rechercher par</label>
              <SearchTypeSelect
                value={tempOptionSearch}
                onChange={setTempOptionSearch}
                options={COMPANY_SEARCH_OPTIONS}
                className="rounded-lg"
              />
            </div>
            <Button color="PRIMARY" size="FULL" onClick={applyFilters} className="w-full mt-2">
              Appliquer
            </Button>
          </div>
        </BottomSheet>
    </PageForm>
  );
};

export default Company;
